import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppBar, Toolbar, Typography, Button, Box, CircularProgress, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PlatformView from './pages/PlatformView';
import WarehouseView from './pages/WarehouseView';
import OBDSignOn from './pages/OBDSignOn';
import OBDDashboard from './pages/OBDDashboard';
import ProductivityTracking from './pages/ProductivityTracking';
import QualityControl from './pages/QualityControl';
import InventoryAnalysis from './pages/InventoryAnalysis';
import KPITracking from './pages/KPITracking';
import KPIDashboard from './pages/KPIDashboard';
import BackroomScans from './pages/BackroomScans';
import Login from './pages/Login';
import Configuration from './pages/Configuration';
import Feedback from './pages/Feedback';
import Display from './pages/Display';
import Assignments from './pages/Assignments';
import Reports from './pages/Reports';
import PutawaySignOn from './pages/PutawaySignOn';
import LaborPlanner from './pages/LaborPlanner';
import ClockIn from './pages/ClockIn';
import { DataProvider } from './contexts/DataContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';

// Logo import
import PulseLogoBanner from './assets/PULSE Logo Banner.png';
import DeparturePopup from './components/DeparturePopup';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Protected Route wrapper component
// requireManager = Manager OR Admin can access (OBD Dashboard)
// requireAdmin = Only Admin can access (Inventory, KPI, Config, etc.)
// requireKPI = Admin OR Report can access (KPI tab)
// requireKPIDashboard = Admin, Manager, OR Report can access (KPI Dashboard)
function ProtectedRoute({ children, requireManager = false, requireAdmin = false, requireKPI = false, requireKPIDashboard = false }) {
  const { currentUser, loading, canAccessManagerFeatures, canAccessAllFeatures, canAccessKPI, canAccessKPIDashboard, isReport } = useAuth();

  // Determine default route based on role
  const getDefaultRoute = () => {
    if (isReport) return '/kpi-dashboard';
    return '/obd-sign-on';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // KPI Dashboard routes (admin, manager, or report)
  if (requireKPIDashboard && !canAccessKPIDashboard) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  // KPI-accessible routes (admin or report)
  if (requireKPI && !canAccessKPI) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  // Admin-only routes
  if (requireAdmin && !canAccessAllFeatures) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  // Manager or Admin routes
  if (requireManager && !canAccessManagerFeatures) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  return children;
}

function Navigation() {
  const navigate = useNavigate();
  const { currentUser, logout, isAdmin, isReport, canAccessManagerFeatures, canAccessAllFeatures, canAccessKPI, canAccessKPIDashboard, isTabVisible } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Don't show navigation if not logged in
  if (!currentUser) return null;

  // Kiosk mode: the display (wall-board) role gets no navigation at all
  if (currentUser.role === 'display') return null;

  // Get role color for badge
  const getRoleColor = () => {
    switch (currentUser.role) {
      case 'admin': return '#d32f2f'; // Red for admin
      case 'manager': return '#4caf50'; // Green for manager
      case 'report': return '#9c27b0'; // Purple for report
      default: return '#2196f3'; // Blue for associate
    }
  };

  return (
    <AppBar position="static">
      <Toolbar sx={{ '& .MuiButton-root': { fontSize: '0.7rem', minWidth: 'auto', px: 0.75, whiteSpace: 'nowrap' } }}>
        <img
          src={PulseLogoBanner}
          alt="PULSE"
          style={{ height: 50, maxHeight: 50, marginRight: 16 }}
        />

        {/* OBD Sign On - Available to all authenticated users except report role */}
        {!isReport && isTabVisible('obd-sign-on') && (
          <Button color="inherit" onClick={() => navigate('/obd-sign-on')}>
            OBD Sign On
          </Button>
        )}

        {/* Putaway Sign On - Available to all authenticated users */}
        {isTabVisible('putaway') && (
          <Button color="inherit" onClick={() => navigate('/putaway')}>
            Putaway Sign On
          </Button>
        )}

        {/* Activity Monitor - Available to all authenticated users except report role */}
        {!isReport && isTabVisible('clock-in') && (
          <Button color="inherit" onClick={() => navigate('/clock-in')}>
            Activity Monitor
          </Button>
        )}

        {/* Manager and Admin: OBD Dashboard, Reports, Labor Planner, and Config */}
        {canAccessManagerFeatures && (
          <>
            {isTabVisible('display') && (
              <Button color="inherit" onClick={() => navigate('/display')}>
                Display
              </Button>
            )}
            {isTabVisible('obd-dashboard') && (
              <Button color="inherit" onClick={() => navigate('/obd-dashboard')}>
                DC Dashboard
              </Button>
            )}
            {isTabVisible('assignments') && (
              <Button color="inherit" onClick={() => navigate('/assignments')}>
                Assignments
              </Button>
            )}
            {isTabVisible('reports') && (
              <Button color="inherit" onClick={() => navigate('/reports')}>
                Reports
              </Button>
            )}
            {isTabVisible('labor-planner') && (
              <Button color="inherit" onClick={() => navigate('/labor-planner')}>
                Labor Planner
              </Button>
            )}
            {isTabVisible('configuration') && (
              <Button color="inherit" onClick={() => navigate('/configuration')}>
                Config
              </Button>
            )}
          </>
        )}

        {/* Report role: KPI only */}
        {isReport && isTabVisible('kpi') && (
          <Button color="inherit" onClick={() => navigate('/kpi')}>
            KPI
          </Button>
        )}

        {/* KPI Dashboard - Admin, Manager, and Report roles */}
        {canAccessKPIDashboard && isTabVisible('kpi-dashboard') && (
          <Button color="inherit" onClick={() => navigate('/kpi-dashboard')}>
            KPI Dashboard
          </Button>
        )}

        {/* Admin only: All other tabs */}
        {canAccessAllFeatures && (
          <>
            {isTabVisible('platform') && (
              <Button color="inherit" onClick={() => navigate('/platform')}>
                Platform
              </Button>
            )}
            {isTabVisible('warehouse') && (
              <Button color="inherit" onClick={() => navigate('/warehouse')}>
                Warehouse
              </Button>
            )}
            {isTabVisible('productivity') && (
              <Button color="inherit" onClick={() => navigate('/productivity')}>
                Productivity
              </Button>
            )}
            {isTabVisible('quality') && (
              <Button color="inherit" onClick={() => navigate('/quality')}>
                Quality
              </Button>
            )}
            {isTabVisible('inventory') && (
              <Button color="inherit" onClick={() => navigate('/inventory')}>
                Inventory
              </Button>
            )}
            {isTabVisible('kpi') && (
              <Button color="inherit" onClick={() => navigate('/kpi')}>
                KPI
              </Button>
            )}
            {isTabVisible('backroom-scans') && (
              <Button color="inherit" onClick={() => navigate('/backroom-scans')}>
                Space
              </Button>
            )}
            {isTabVisible('feedback') && (
              <Button color="inherit" onClick={() => navigate('/feedback')}>
                Feedback
              </Button>
            )}
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* User info and logout */}
        <Chip
          icon={<PersonIcon />}
          label={`${currentUser.fullName || currentUser.username} (${currentUser.role})`}
          sx={{
            mr: 2,
            backgroundColor: getRoleColor(),
            color: 'white',
            '& .MuiChip-icon': { color: 'white' }
          }}
        />
        <Button
          color="inherit"
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
        >
          Logout
        </Button>
      </Toolbar>
    </AppBar>
  );
}

// Default redirect based on user role
function DefaultRedirect() {
  const { currentUser, loading, isReport } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Display (wall-board) accounts go straight to the board, kiosk-style
  if (currentUser.role === 'display') {
    return <Navigate to="/display" replace />;
  }

  // Report users go to KPI Dashboard, everyone else to OBD Sign On
  if (isReport) {
    return <Navigate to="/kpi-dashboard" replace />;
  }

  return <Navigate to="/obd-sign-on" replace />;
}

function App() {
  const userContext = {
    role: 'warehouse',
    platform: null,
    permissions: {
      view_all_data: true,
      kpiTracking: true,
      backroomScans: true,
      platformView: true,
      warehouseView: true,
      obdSignOn: true,
      productivity: true,
      quality: true,
      inventoryAnalysis: true,
      configuration: true
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <DataProvider>
            <Box sx={{ flexGrow: 1 }}>
              <Navigation />
              <DeparturePopup />
              <Routes>
                {/* Public route - Login */}
                <Route path="/login" element={<Login />} />

                {/* Default redirect - handled by DefaultRedirect component */}
                <Route path="/" element={<DefaultRedirect />} />

                {/* OBD Sign On - Available to all authenticated users */}
                <Route path="/obd-sign-on" element={
                  <ProtectedRoute>
                    <OBDSignOn />
                  </ProtectedRoute>
                } />
                <Route path="/signin" element={<Navigate to="/obd-sign-on" replace />} />

                {/* Display — wall-screen floor board (display/manager/admin) */}
                <Route path="/display" element={
                  <ProtectedRoute>
                    <Display />
                  </ProtectedRoute>
                } />

                {/* Assignments — lead-controlled OBD auto-assignment (manager/admin) */}
                <Route path="/assignments" element={
                  <ProtectedRoute>
                    <Assignments />
                  </ProtectedRoute>
                } />

                {/* Putaway Sign On - Available to all authenticated users */}
                <Route path="/putaway" element={
                  <ProtectedRoute>
                    <PutawaySignOn />
                  </ProtectedRoute>
                } />

                {/* Time Clock - Available to all authenticated users */}
                <Route path="/clock-in" element={
                  <ProtectedRoute>
                    <ClockIn />
                  </ProtectedRoute>
                } />

                {/* Manager + Admin routes */}
                <Route path="/obd-dashboard" element={
                  <ProtectedRoute requireManager>
                    <OBDDashboard />
                  </ProtectedRoute>
                } />

                {/* Labor Planner - Manager + Admin */}
                <Route path="/labor-planner" element={
                  <ProtectedRoute requireManager>
                    <LaborPlanner />
                  </ProtectedRoute>
                } />

                {/* Admin-only routes */}
                <Route path="/platform" element={
                  <ProtectedRoute requireAdmin>
                    <PlatformView userContext={userContext} />
                  </ProtectedRoute>
                } />
                <Route path="/warehouse" element={
                  <ProtectedRoute requireAdmin>
                    <WarehouseView userContext={userContext} />
                  </ProtectedRoute>
                } />
                <Route path="/productivity" element={
                  <ProtectedRoute requireAdmin>
                    <ProductivityTracking />
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute requireManager>
                    <Reports />
                  </ProtectedRoute>
                } />
                <Route path="/quality" element={
                  <ProtectedRoute requireAdmin>
                    <QualityControl />
                  </ProtectedRoute>
                } />
                <Route path="/inventory" element={
                  <ProtectedRoute requireAdmin>
                    <InventoryAnalysis />
                  </ProtectedRoute>
                } />
                <Route path="/kpi" element={
                  <ProtectedRoute requireKPI>
                    <KPITracking />
                  </ProtectedRoute>
                } />
                <Route path="/kpi-dashboard" element={
                  <ProtectedRoute requireKPIDashboard>
                    <KPIDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/backroom-scans" element={
                  <ProtectedRoute requireAdmin>
                    <BackroomScans />
                  </ProtectedRoute>
                } />
                <Route path="/configuration" element={
                  <ProtectedRoute requireManager>
                    <Configuration />
                  </ProtectedRoute>
                } />
                <Route path="/feedback" element={
                  <ProtectedRoute requireAdmin>
                    <Feedback />
                  </ProtectedRoute>
                } />

                {/* Catch-all redirect */}
                <Route path="*" element={<Navigate to="/obd-sign-on" replace />} />
              </Routes>
            </Box>
          </DataProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;